/**
 * Page: HubLoginPage (hub.usetenon.com)
 * Tenon employees authenticate against platform_admins; the issued JWT carries
 * aud="hub" — entirely separate from the app login flow.
 */
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { hubLogin, setHubToken } from './api'
import tenonAppIcon from '@/assets/brand/tenon-app-icon.png'
import { posthogCapture } from './analytics'

const P = {
  pageBg:     'var(--bg-page)',
  card:       'var(--bg-surface)',
  border:     'var(--border)',
  text:       'var(--text-primary)',
  textMuted:  'var(--text-secondary)',
  textDim:    'var(--text-muted)',
  inputBg:    'var(--bg-elevated)',
  gold:       'var(--brand-gold)',
  goldText:   'var(--bg-page)',
  font:       "'Karla', -apple-system, sans-serif",
}

export default function HubLoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    document.title = 'Tenon Hub — Sign in'
  }, [])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const res = await hubLogin(email.trim(), password)
      setHubToken(res.token)
      posthogCapture('form_submission', { form: 'hub_login', email: email.trim() })
      navigate('/queue', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: P.pageBg, fontFamily: P.font, padding: '24px 16px',
    }}>
      <div style={{ width: '100%', maxWidth: 420, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32 }}>
          <img src={tenonAppIcon} alt="Tenon" style={{ width: 44, height: 44 }} />
          <span style={{ fontSize: 22, fontWeight: 600, color: P.text, letterSpacing: '-0.02em' }}>Tenon Hub</span>
        </div>

        <div style={{
          width: '100%', background: P.card, border: `1px solid ${P.border}`,
          borderRadius: 16, padding: 40, boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
        }}>
          <h1 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 600, color: P.text }}>Platform admin sign-in</h1>
          <p style={{ margin: '0 0 24px', fontSize: 14, color: P.textMuted }}>
            Restricted to Tenon employees. App users sign in at <a href="https://app.usetenon.com/login" style={{ color: P.text, textDecoration: 'underline' }}>app.usetenon.com</a>.
          </p>

          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 13, color: P.textMuted }}>Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                required
                style={{
                  background: P.inputBg, border: `1px solid ${P.border}`, borderRadius: 8,
                  padding: '10px 14px', fontSize: 15, color: P.text, fontFamily: 'inherit',
                }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 13, color: P.textMuted }}>Password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{
                  background: P.inputBg, border: `1px solid ${P.border}`, borderRadius: 8,
                  padding: '10px 14px', fontSize: 15, color: P.text, fontFamily: 'inherit',
                }}
              />
            </label>

            {error && (
              <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(220,80,80,0.10)', color: '#c33', fontSize: 14 }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !email || !password}
              style={{
                marginTop: 8, padding: '12px 20px', borderRadius: 10,
                background: P.gold, color: P.goldText, border: 'none',
                fontSize: 15, fontWeight: 600, fontFamily: 'inherit',
                cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </div>

        <p style={{ marginTop: 20, fontSize: 12, color: P.textDim, textAlign: 'center' }}>
          aud=hub · 8h session · audit-logged
        </p>
      </div>
    </div>
  )
}
